import { eachDayOfInterval, format, parseISO } from 'date-fns'
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { ROOT_EVENT_SLUG } from '@/components/VisitorProfiles/config'
import {
  useEventBySlugQuery,
  useMeetingsCalendarAvailabilitiesQuery,
  useMeetingsBulkUpdateCalendarAvailabilitiesMutation,
} from '@/gql/myomr'

export interface LocalInterval {
  id?: string
  tempId: string
  date: string
  startTime: string
  endTime: string
  concurrency: string
  isNew: boolean
  isDeleted: boolean
}

export interface EventDay {
  date: string
}

const SLOT_DURATION = 30
const DAY_START_HOUR = 9
const DAY_END_HOUR = 18
const TRACK_COUNT = 5

function toUTCISOString(date: string, time: string): string {
  const localDate = new Date(`${date}T${time}:00`)
  return localDate.toISOString()
}

/** Generate 30-min slot labels from 09:00 to 17:30 */
export function generateTimeSlots(): string[] {
  const slots: string[] = []
  for (let hour = DAY_START_HOUR; hour < DAY_END_HOUR; hour++) {
    for (const minute of [0, 30]) {
      slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`)
    }
  }
  return slots
}

function addMinutesToTime(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number)
  const total = h * 60 + m + minutes
  const newH = Math.floor(total / 60)
  const newM = total % 60
  return `${newH.toString().padStart(2, '0')}:${newM.toString().padStart(2, '0')}`
}

export function useMeetingsConfiguration() {
  const { t, locale } = useI18n()
  const showToast = inject<(message: string, type: 'success' | 'error') => void>('showToast', () => {})

  // State
  const localIntervals = ref<LocalInterval[]>([])
  const descriptionDe = ref('Vereinbare einen Termin mit uns und wir finden heraus, wie wir dir am besten helfen können.')
  const descriptionEn = ref('Schedule an appointment with us, and together we will discover how we can best assist you.')
  const saving = ref(false)

  // Grid state
  const selectedDay = ref('')
  const grid = ref<Record<string, boolean[][]>>({})
  const timeSlotLabels = generateTimeSlots()

  // GraphQL
  const { result: eventResult, loading: eventLoading } = useEventBySlugQuery({ slug: ROOT_EVENT_SLUG })

  const { result, loading: availLoading, refetch } = useMeetingsCalendarAvailabilitiesQuery({ eventSlug: ROOT_EVENT_SLUG })

  const { mutate: bulkUpdate } = useMeetingsBulkUpdateCalendarAvailabilitiesMutation()

  const loading = computed(() => availLoading.value)

  // Event days
  const eventDays = computed<EventDay[]>(() => {
    const event = eventResult.value?.eventBySlug
    if (!event?.startsAt || !event?.endsAt) return []
    const start = parseISO(event.startsAt)
    const end = parseISO(event.endsAt)
    return eachDayOfInterval({ start, end }).map((day) => ({ date: format(day, 'yyyy-MM-dd') }))
  })

  watch(
    eventDays,
    (days) => {
      if (days.length > 0 && !selectedDay.value) {
        selectedDay.value = days[0].date
      }
    },
    { immediate: true },
  )

  // Current day's grid for v-model
  const currentDayGrid = computed({
    get: () => grid.value[selectedDay.value] ?? [],
    set: (val) => {
      grid.value[selectedDay.value] = val
    },
  })

  // ── Grid helpers ──

  function createEmptyGrid(): boolean[][] {
    return timeSlotLabels.map(() => Array.from({ length: TRACK_COUNT }, () => false))
  }

  function initializeGrids() {
    for (const day of eventDays.value) {
      if (!grid.value[day.date]) {
        grid.value[day.date] = createEmptyGrid()
      }
    }
  }

  /** Hydrate grid from loaded CalendarAvailability records (works with both merged and per-slot records). */
  function intervalsToGrid() {
    for (const day of eventDays.value) {
      const dayIntervals = localIntervals.value.filter((i) => i.date === day.date && !i.isDeleted)
      const dayGrid = createEmptyGrid()

      for (const interval of dayIntervals) {
        const startIdx = timeSlotLabels.indexOf(interval.startTime)
        if (startIdx === -1) continue
        const concurrency = Math.min(parseInt(interval.concurrency), TRACK_COUNT)

        for (let s = startIdx; s < timeSlotLabels.length; s++) {
          if (timeSlotLabels[s] >= interval.endTime) break
          for (let t = 0; t < concurrency; t++) {
            dayGrid[s][t] = true
          }
        }
      }

      grid.value[day.date] = dayGrid
    }
  }

  // ── Smart diff: grid → mutation periods ──

  /**
   * Compare grid state against existing DB records and produce a minimal
   * set of create / update / destroy operations for bulkUpdate.
   */
  function isSingleSlot(interval: LocalInterval): boolean {
    return interval.endTime === addMinutesToTime(interval.startTime, SLOT_DURATION)
  }

  function syncGridToIntervals() {
    // Index existing per-slot records by "date|startTime". Merged records go straight to destroy.
    const existingMap = new Map<string, LocalInterval>()
    for (const interval of localIntervals.value) {
      if (!interval.isNew) {
        if (isSingleSlot(interval)) {
          existingMap.set(`${interval.date}|${interval.startTime}`, interval)
        } else {
          // Merged record (covers >1 slot) → destroy and replace with per-slot creates
          interval.isDeleted = true
        }
      }
    }

    // Remove previously generated "new" intervals from a prior sync
    localIntervals.value = localIntervals.value.filter((i) => !i.isNew)

    for (const day of eventDays.value) {
      const dayGrid = grid.value[day.date]
      if (!dayGrid) continue

      for (let s = 0; s < timeSlotLabels.length; s++) {
        const concurrency = dayGrid[s].filter(Boolean).length
        const key = `${day.date}|${timeSlotLabels[s]}`
        const existing = existingMap.get(key)

        if (concurrency > 0 && existing) {
          // Existing per-slot record still checked → update concurrency (backend skips if unchanged)
          existing.concurrency = String(concurrency)
          existing.isDeleted = false
          existingMap.delete(key)
        } else if (concurrency > 0) {
          // New slot → create
          localIntervals.value.push({
            tempId: `grid-${key}`,
            date: day.date,
            startTime: timeSlotLabels[s],
            endTime: addMinutesToTime(timeSlotLabels[s], SLOT_DURATION),
            concurrency: String(concurrency),
            isNew: true,
            isDeleted: false,
          })
        } else if (existing) {
          // Slot unchecked → destroy
          existing.isDeleted = true
          existingMap.delete(key)
        }
      }
    }

    // Remaining unmatched existing records → destroy (e.g. old merged records that don't align to a slot)
    for (const interval of existingMap.values()) {
      interval.isDeleted = true
    }
  }

  // ── Reset / Load ──

  function resetLocalState() {
    const nodes = result.value?.meetings?.calendarAvailabilities?.nodes
    if (!nodes) return

    localIntervals.value = nodes.map((node) => {
      const startsAt = parseISO(node.startsAt)
      const endsAt = parseISO(node.endsAt)
      return {
        id: node.id,
        tempId: node.id,
        date: format(startsAt, 'yyyy-MM-dd'),
        startTime: format(startsAt, 'HH:mm'),
        endTime: format(endsAt, 'HH:mm'),
        concurrency: String(node.concurrency ?? 1),
        isNew: false,
        isDeleted: false,
      }
    })

    const firstNode = nodes[0]
    if (firstNode?.exhibitorCalendar?.descriptionDe) {
      descriptionDe.value = firstNode.exhibitorCalendar.descriptionDe
    }
    if (firstNode?.exhibitorCalendar?.descriptionEn) {
      descriptionEn.value = firstNode.exhibitorCalendar.descriptionEn
    }

    initializeGrids()
    intervalsToGrid()
  }

  watch(
    () => result.value?.meetings?.calendarAvailabilities?.nodes,
    () => resetLocalState(),
    { immediate: true },
  )

  watch(eventDays, () => {
    initializeGrids()
    if (localIntervals.value.length > 0) {
      intervalsToGrid()
    }
  })

  // ── Save ──

  /** Build the periods array for the bulkUpdate mutation from localIntervals. */
  function buildPeriods() {
    return localIntervals.value
      .filter((i) => !(i.isNew && i.isDeleted)) // skip new-then-deleted (no-op)
      .map((interval) => {
        if (interval.isDeleted && interval.id) {
          return { calendarAvailabilityId: interval.id, _destroy: true }
        }
        const base = {
          startsAt: toUTCISOString(interval.date, interval.startTime),
          endsAt: toUTCISOString(interval.date, interval.endTime),
          concurrency: parseInt(interval.concurrency),
        }
        return interval.id ? { calendarAvailabilityId: interval.id, ...base } : base
      })
  }

  async function checkForWarnings(): Promise<string[]> {
    syncGridToIntervals()

    const periods = buildPeriods()
    if (periods.length === 0) return []

    const dryRunResult = await bulkUpdate({ eventSlug: ROOT_EVENT_SLUG, periods, dryRun: true })

    if (dryRunResult?.data?.meetingsBulkUpdateCalendarAvailabilities?.errors?.length) {
      const errors = dryRunResult.data.meetingsBulkUpdateCalendarAvailabilities.errors
      errors.forEach((error) => showToast(error, 'error'))
      throw new Error('Validation errors')
    }

    const warnings = dryRunResult?.data?.meetingsBulkUpdateCalendarAvailabilities?.warnings || []
    return warnings.filter((w): w is string => w !== null)
  }

  async function performSave(): Promise<boolean> {
    try {
      saving.value = true

      const periods = buildPeriods()

      if (periods.length > 0) {
        const updateResult = await bulkUpdate({
          eventSlug: ROOT_EVENT_SLUG,
          periods,
          dryRun: false,
          descriptionDe: descriptionDe.value || undefined,
          descriptionEn: descriptionEn.value || undefined,
        })

        if (updateResult?.data?.meetingsBulkUpdateCalendarAvailabilities?.errors?.length) {
          const errors = updateResult.data.meetingsBulkUpdateCalendarAvailabilities.errors
          errors.forEach((error) => showToast(error, 'error'))
          return false
        }
      }

      showToast(t('common.changesSaved'), 'success')
      await refetch()
      return true
    } finally {
      saving.value = false
    }
  }

  // ── Preview ──

  const previewSlots = computed(() => {
    const slots: Array<{ id: string; startsAt: string; endsAt: string; available: boolean }> = []

    for (const day of eventDays.value) {
      const dayGrid = grid.value[day.date]
      if (!dayGrid) continue

      for (let s = 0; s < timeSlotLabels.length; s++) {
        if (!dayGrid[s]?.some(Boolean)) continue

        const time = timeSlotLabels[s]
        const endTime = addMinutesToTime(time, SLOT_DURATION)
        const [h, m] = time.split(':').map(Number)
        const [eh, em] = endTime.split(':').map(Number)

        slots.push({
          id: `preview-${day.date}-${h}-${m}`,
          startsAt: `${day.date}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00+02:00`,
          endsAt: `${day.date}T${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}:00+02:00`,
          available: true,
        })
      }
    }

    return slots
  })

  const previewDescription = computed(() => (locale.value === 'de' ? descriptionDe.value : descriptionEn.value))

  function formatDate(dateStr: string): string {
    const date = parseISO(dateStr)
    return format(date, 'EEE, MMM d')
  }

  return {
    descriptionDe,
    descriptionEn,
    saving,
    loading,
    eventLoading,
    selectedDay,
    grid,
    currentDayGrid,
    timeSlotLabels,
    eventDays,
    previewSlots,
    previewDescription,
    formatDate,
    syncGridToIntervals,
    checkForWarnings,
    performSave,
    resetLocalState,
  }
}
